"use client";

import { useEffect, useState, type ReactNode } from "react";

/**
 * One answer per page load for every place that asks (the header, the phone menu and the footer share it), so a visitor costs one
 * request, and none at all when the site has nowhere to ask.
 */
let asked: { href: string; answer: Promise<boolean> } | null = null;

/**
 * Whether the visitor is signed in, from the storefront's `{ signedIn }` answer. The session cookie is HttpOnly, so the page asks
 * the server, and the page the server sends is the same for everyone. Any failure (offline, an older storefront, a rate limit)
 * reads as signed out: the link just stays "Sign in".
 */
export function askSignedIn(href: string, fetcher: typeof fetch = fetch): Promise<boolean> {
  if (asked?.href === href) return asked.answer;
  const answer = fetcher(href, { credentials: "same-origin", cache: "no-store", headers: { Accept: "application/json" } })
    .then(async (response) => {
      if (!response.ok) return false;
      const body: unknown = await response.json().catch(() => null);
      return typeof body === "object" && body !== null && (body as { signedIn?: unknown }).signedIn === true;
    })
    .catch(() => false);
  asked = { href, answer };
  return answer;
}

export const forgetSignedIn = () => {
  asked = null;
};

function useSignedIn(statusHref: string | null): boolean {
  const [signedIn, setSignedIn] = useState(false);
  useEffect(() => {
    if (!statusHref) return;
    let live = true;
    const check = () => {
      void askSignedIn(statusHref).then((answer) => {
        if (live) setSignedIn(answer);
      });
    };
    check();
    // A page restored from the back/forward cache may be from before someone signed out or in: ask again.
    const restored = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      forgetSignedIn();
      check();
    };
    window.addEventListener("pageshow", restored);
    return () => {
      live = false;
      window.removeEventListener("pageshow", restored);
    };
  }, [statusHref]);
  return signedIn;
}

/**
 * A sign-in link that becomes the visitor's account link once the server says they are signed in. The first paint (and the HTML
 * that is sent) is always "Sign in", so a cached page never carries anybody's state.
 */
export function SessionLink({
  statusHref,
  signInHref,
  accountHref,
  signInLabel,
  accountLabel,
  className,
  icon,
  labelInSpan = false,
}: {
  statusHref: string | null;
  signInHref: string;
  accountHref: string;
  signInLabel: string;
  accountLabel: string;
  className?: string;
  icon?: ReactNode;
  /** The header's link underlines the word on hover, so its label sits in a span. */
  labelInSpan?: boolean;
}) {
  const signedIn = useSignedIn(statusHref);
  const label = signedIn ? accountLabel : signInLabel;
  return (
    <a className={className} href={signedIn ? accountHref : signInHref}>
      {icon}
      {labelInSpan ? <span>{label}</span> : label}
    </a>
  );
}
